const dialogMenu = document.createElement("template");
const dialogTrigger = document.createElement("template");
const dialogContent = document.createElement("template");
const dialogHeader = document.createElement("template");
const dialogTitle = document.createElement("template");
const dialogDescription = document.createElement("template");
const dialogBody = document.createElement("template");
const dialogSeparator = document.createElement("template");
const dialogFooter = document.createElement("template");

dialogMenu.innerHTML = `  
      <slot name="dialog-trigger"></slot>
      <slot name="dialog-content"></slot>
    `;

class DialogMenu extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" }).append(
      dialogMenu.content.cloneNode(true)
    );

    this.isOpen = false;
    this._onTriggerClick = this._onTriggerClick.bind(this);
    this._onEscapeKey = this._onEscapeKey.bind(this);
    this._onOverlayClick = this._onOverlayClick.bind(this);

    // Create dialog overlay
    this.dialogOverlay = document.createElement("dialog-overlay");
    this.dialogOverlay.setAttribute("data-state", "closed");
    this.dialogOverlay.setAttribute("role", "presentation");
    this.dialogOverlay.addEventListener("click", this._onOverlayClick);
  }

  connectedCallback() {
    this.dialogTrigger = this.querySelector("dialog-trigger");
    this.dialogContent = this.querySelector("dialog-content");
    this.dialogCloseTrigger = this.querySelector(".dialog-close-trigger");

    if (!this.dialogCloseTrigger) {
      this._addCloseButton();
    } else {
      this.dialogCloseTrigger.addEventListener("click", this.close.bind(this));
    }

    if (!this.dialogTrigger || !this.dialogContent) {
      console.warn(
        "<dialog-menu> needs a <dialog-trigger> and <dialog-content>"
      );
      return;
    }

    // Set up size-based styling
    this._setupSize();

    // Detach the content and overlay so they don't live in the DOM until open()
    this.dialogOverlay.remove();
    this.dialogContent.remove();

    this._updateAriaExpanded();

    this.dialogTrigger.addEventListener("click", this._onTriggerClick);
  }

  disconnectedCallback() {
    if (this.dialogOverlay && this.dialogOverlay.parentNode) {
      this.dialogOverlay.remove();
    }
    if (this.dialogContent && this.dialogContent.parentNode) {
      this.dialogContent.remove();
    }

    this.dialogTrigger?.removeEventListener("click", this._onTriggerClick);
    if (this.dialogCloseTrigger) {
      this.dialogCloseTrigger.removeEventListener(
        "click",
        this.close.bind(this)
      );
    }
    document.removeEventListener("keydown", this._onEscapeKey);
  }

  _setupSize() {
    // Get the size from the data attribute, default to 'md'
    const size = this.getAttribute("data-size") || "md";

    // Apply size attribute to dialog content for CSS targeting
    this.dialogContent.setAttribute("data-size", size);
  }

  _onEscapeKey(e) {
    if (e.key === "Escape" && this.isOpen) {
      this.close();
    }
  }

  _onOverlayClick(e) {
    // Only close if clicking the overlay itself, not its children
    if (e.target === this.dialogOverlay) {
      this.close();
    }
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  _updateAriaExpanded() {
    if (this.dialogTrigger) {
      this.dialogTrigger.setAttribute("aria-expanded", String(this.isOpen));
    }
  }

  open() {
    if (this.isOpen) return;

    this.isOpen = true;
    this._updateAriaExpanded();

    // Append the overlay
    document.body.appendChild(this.dialogOverlay);

    // Move content inside the overlay
    this.dialogOverlay.appendChild(this.dialogContent);

    // Add escape key listener
    document.addEventListener("keydown", this._onEscapeKey);

    // Trigger reflow to ensure animations start properly after state changes
    this.dialogOverlay.offsetHeight;
    this.dialogContent.offsetHeight;

    // Update states to open, triggering CSS animations
    this.dialogOverlay.setAttribute("data-state", "open");
    this.dialogContent.setAttribute("data-state", "open");

    // Lock body scroll
    document.body.setAttribute("data-dialog-scroll-locked", "");

    // Focus management - focus the dialog content
    this.dialogContent.focus();

    this.dispatchEvent(
      new CustomEvent("dialog:open", {
        bubbles: true,
        detail: { dialog: this },
      })
    );
  }

  close() {
    if (!this.isOpen) return;

    this.isOpen = false;
    this._updateAriaExpanded();

    this.dialogOverlay.setAttribute("data-state", "closed");
    this.dialogContent.setAttribute("data-state", "closed");

    // Remove escape key listener
    document.removeEventListener("keydown", this._onEscapeKey);

    setTimeout(() => {
      // Remove the entire overlay (which contains the content)
      if (this.dialogOverlay.parentNode) {
        this.dialogOverlay.remove();
      }

      // Put the content back in the dialog menu, but detached from DOM
      if (this.dialogContent.parentNode) {
        this.dialogContent.remove();
      }

      document.body.removeAttribute("data-dialog-scroll-locked");
    }, 200); // Match animation duration

    this.dispatchEvent(
      new CustomEvent("dialog:close", {
        bubbles: true,
        detail: { dialog: this },
      })
    );
  }

  _onTriggerClick(e) {
    e.preventDefault();
    this.toggle();
  }

  _addCloseButton() {
    const closeButton = document.createElement("button");
    closeButton.className = "dialog-close-trigger";
    closeButton.setAttribute("aria-label", "Close dialog");
    closeButton.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
      </svg>
    `;
    closeButton.addEventListener("click", this.close.bind(this));
    this.dialogContent.appendChild(closeButton);
    this.dialogCloseTrigger = closeButton;
  }
}

customElements.define("dialog-menu", DialogMenu);

dialogTrigger.innerHTML = `
      <slot></slot>
`;

class DialogTrigger extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" }).append(
      dialogTrigger.content.cloneNode(true)
    );
    this.setAttribute("slot", "dialog-trigger");
    this.setAttribute("aria-haspopup", "dialog");
    this.setAttribute("role", "button");
    this.setAttribute("tabindex", "0");
  }

  connectedCallback() {
    // Handle keyboard activation
    this.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        this.click();
      }
    });
  }
}

customElements.define("dialog-trigger", DialogTrigger);

dialogContent.innerHTML = `
      <slot></slot>
    `;

class DialogContent extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" }).append(
      dialogContent.content.cloneNode(true)
    );

    this.setAttribute("slot", "dialog-content");
    this.setAttribute("role", "dialog");
    this.setAttribute("aria-modal", "true");
    this.setAttribute("tabindex", "-1");
    this.setAttribute("data-state", "closed");
  }
}

customElements.define("dialog-content", DialogContent);

dialogHeader.innerHTML = `
      <slot></slot>
    `;

class DialogHeader extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" }).append(
      dialogHeader.content.cloneNode(true)
    );
  }
}

customElements.define("dialog-header", DialogHeader);

dialogTitle.innerHTML = `
      <slot></slot>
    `;

class DialogTitle extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" }).append(
      dialogTitle.content.cloneNode(true)
    );

    this.setAttribute("role", "heading");
    this.setAttribute("aria-level", "2");
  }
}

customElements.define("dialog-title", DialogTitle);

dialogDescription.innerHTML = `
      <slot></slot>
    `;

class DialogDescription extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" }).append(
      dialogDescription.content.cloneNode(true)
    );
  }
}

customElements.define("dialog-description", DialogDescription);

dialogBody.innerHTML = `
      <slot></slot>
    `;

class DialogBody extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" }).append(
      dialogBody.content.cloneNode(true)
    );
  }
}

customElements.define("dialog-body", DialogBody);

dialogFooter.innerHTML = `
      <slot></slot>
    `;

class DialogFooter extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" }).append(
      dialogFooter.content.cloneNode(true)
    );
  }
}

customElements.define("dialog-footer", DialogFooter);

dialogSeparator.innerHTML = ``;

class DialogSeparator extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" }).append(
      dialogSeparator.content.cloneNode(true)
    );
    this.setAttribute("role", "separator");
  }
}

customElements.define("dialog-separator", DialogSeparator);
